import { useEffect, useState, useRef } from "react";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import "../../style/scss/FormInput.scss";

function fieldValueFromProps(props) {
  if (props.value !== undefined && props.value !== null) return props.value;
  if (props.field && props.formObject?.object) {
    const obj = props.formObject.object;
    const fromObject = obj[props.field];
    const camel =
      props.field.charAt(0).toLowerCase() + props.field.slice(1);
    const fromCamel = camel !== props.field ? obj[camel] : undefined;
    // Prefer a non-empty camelCase value when PascalCase is missing or empty
    // (API JSON is often camelCase while form fields use PascalCase).
    if (fromObject !== undefined && fromObject !== null && fromObject !== "") {
      return fromObject;
    }
    if (fromCamel !== undefined && fromCamel !== null && fromCamel !== "") {
      return fromCamel;
    }
    if (fromObject !== undefined && fromObject !== null) return fromObject;
    if (fromCamel !== undefined && fromCamel !== null) return fromCamel;
  }
  return "";
}

export default function FormInput(props) {
  const [value, setValue] = useState(() => fieldValueFromProps(props));
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);
  const isPassword = props.type === "password";
  const objectFieldValue =
    props.field && props.formObject?.object ? props.formObject.object[props.field] : undefined;
  const camelField =
    props.field && props.field.length > 0
      ? props.field.charAt(0).toLowerCase() + props.field.slice(1)
      : undefined;
  const objectCamelValue =
    camelField && props.formObject?.object ? props.formObject.object[camelField] : undefined;

  useEffect(() => {
    if (props.autofocus && inputRef.current) inputRef.current.focus();
  }, [props.autofocus]);

  useEffect(() => {
    setValue(fieldValueFromProps(props));
  }, [props.value, props.field, objectFieldValue, objectCamelValue]);

  useEffect(() => {
    const next = fieldValueFromProps(props);
    if (next !== undefined && next !== null && next !== "") {
      setIsFocused(true);
    }
  }, [props.value, objectFieldValue, objectCamelValue]);

  const handleChange = (e) => {
    setValue(e.target.value);
    if (props.formObject && props.formObject.onChange) {
      props.formObject.onChange(e.target.value, props.field, "input", e);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const isFieldValid = props.isValid ? props.isValid(value) : true;
  const inputType = isPassword && showPassword ? "text" : props.type;

  const input = (
    <input
      ref={inputRef}
      type={inputType}
      id={props.field}
      name={props.field}
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={`form-input${isPassword ? " form-input--with-toggle" : ""}${
        props.invalid ? " field-invalid-control" : ""
      }`}
      placeholder={props.placeholder}
      autoFocus={props.autofocus}
      autoComplete={props.autocomplete || "off"}
      data-lpignore={props.disablePwManager ? "true" : "false"}
      onInput={handleChange}
      readOnly={props.readonly}
      disabled={props.disabled}
      required={props.required}
    />
  );

  return (
    <div
      className={`field FormInput form-group${props.disablePwManager ? " hide-lastpass" : ""}${
        isPassword ? " form-group--password" : ""
      }${props.invalid ? " field-invalid" : ""}${props.className ? ` ${props.className}` : ""}`}
      hidden={props.hidden || undefined}
    >
      {props.label && (
        <label htmlFor={props.field} className="form-label">
          {props.label}
        </label>
      )}
      {isPassword ? (
        <div className="form-input-wrap">
          {input}
          <button
            type="button"
            className="form-input-toggle"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            tabIndex={-1}
          >
            {showPassword ? <IoEyeOffOutline aria-hidden /> : <IoEyeOutline aria-hidden />}
          </button>
        </div>
      ) : (
        input
      )}
      {!isFieldValid && <span className="error-text">{props.invalidMessage}</span>}
    </div>
  );
}

FormInput.displayName = "FormInput";
